import React, { useContext } from 'react';
import axios from 'axios'
import styles from './re-order.module.scss'
import { StateContext, DispatchContext } from 'src/AppContext'
import { IOrder, IState } from 'src/Interface'
import Button from 'src/components/button/Button';
import { format, isBefore } from 'date-fns';
import { baseApiURL } from 'src/utils/utils'
import { toast } from 'react-toastify';
import { gaEvenActions, gaEvent, gaEventCategories } from 'src/utils/ga/ga';
import { gaOrderedTripMapper, gaOrderTypes, gaUserMapper } from 'src/utils/ga/gaMapper';

interface Props {
  close: () => null
  order: IOrder
}

const ReOrder: React.FunctionComponent<Props> = ({ order, close }: Props) => {
  const state: IState = useContext(StateContext)
  const dispatch: React.Dispatch<any> = useContext(DispatchContext)

  const isBigCar = order.numberOfPeople > 4

  const reOrder = (orderId, e) => {
    e.stopPropagation()
    axios.post(`${baseApiURL()}/orders/${orderId}/re_order`,
      {}, {
      headers: {
        Authorization:
          `Bearer ${state.user.accessToken}`
      }
    }).then((result) => {
      if (isBigCar) {
        toast(
          <div className='tw-flex tw-items-center'>
            <svg className={styles.spinner} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <div>
              <h3>Anmod om storvogn</h3>
              <span>Vær opmærksom på at bestilling af Storvogn kan tage et par minutter før vi kan bekræfte. Vi sender en notifikation når vi har fundet en vogn til jer.</span>
            </div>
          </div>
          , { autoClose: 10000 })
      } else {
        toast('Ordre oprettet')
      }
      gaEvent(gaEventCategories.orders, gaEvenActions.ordered_trip, gaUserMapper(state), gaOrderedTripMapper(gaOrderTypes.re_order_single, null, result.data))
      close()
    }).catch((err) => {
      toast(err.response.data.message)
    })
  }

  const replaceOrder = () => {
    axios(`${baseApiURL()}/current_date`, {
      headers: {
        Authorization:
          `Bearer ${state.user.accessToken}`
      }
    })
      .then((result) => {
        axios.post(`${baseApiURL()}/orders`, {
          bookingModuleUpdatedAt: order.bookingModuleUpdatedAt,
          tripType: 'METERED',
          requestedPickup: isBefore(new Date(order.requestedPickup), new Date(result.data)) ? new Date(result.data).toISOString() : order.requestedPickup,
          numberOfPeople: 4,
          pickupAddress: order.pickupAddress,
          destinationAddress: order.destinationAddress,
          reference: order.reference,
          project: order.project,
          numberOfCars: 2,
          comment: order.comment,
          customerName: order.customerName,
          customerPhone: order.phone,
          accountId: order.account ? order.account.id : null,
          attributeIds: order.attributes && order.attributes.filter(attribute => attribute.code !== 'S5' && attribute.code !== 'S6' && attribute.code !== 'S7' && attribute.code !== 'S8').map(attribute => attribute.id),
        }, {
          headers: {
            Authorization:
              `Bearer ${state.user.accessToken}`
          }
        })
          .then((result) => {
            dispatch({ type: 'SET_USER_ORDERS', userOrders: ([...result.data, ...state.userOrders]) })
            result.data.forEach((orderResult) => gaEvent(gaEventCategories.orders, gaEvenActions.ordered_trip, gaUserMapper(state), gaOrderedTripMapper(gaOrderTypes.re_order_multiple, null, orderResult)))
            close()
          }).catch((err) => {
            toast(err.response.data.message)
          })
      })
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <svg xmlns="http://www.w3.org/2000/svg" className={styles.icon} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
        <h5>{isBigCar ? 'Ingen store vogne tilgængelige' : 'Er du sikker på, at du vil ombestille'}</h5>
        <span className={styles.timeStamp}>{format(new Date(order.requestedPickup), 'HH:mm')}</span>
      </header>
      <div className={styles.content}>
        {isBigCar && <h4><span className='tw-font-normal'>Ønsker du i stedet at bestille</span> <br /><span className='tw-font-medium'>2x alm. vogn (4 personer)?</span></h4>}
        <div className={`${styles.box} ${order.numberOfPeople === 4 ? 'tw-mt-2' : ''}`}>
          <header className='tw-flex'>
            <h6 className={styles.boxTitle}>{order.customerName}</h6>
          </header>
          <p className={`${styles.address} tw-mb-2`}>
            <svg className='tw-w-4 tw-h-4  tw-mr-1' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
            {order.pickupAddress && order.pickupAddress.fullAddress}
          </p>
          {order.destinationAddress && <p className={`${styles.address} tw-opacity-50`}>
            <svg className='tw-w-4 tw-h-4 tw-mr-1' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
            </svg>
            {order.destinationAddress.fullAddress}
          </p>}
        </div>
      </div>
      <footer className={styles.footer}>
        <Button handleClick={close} modifiers='tw-mr-3' type='destructive' label='Annuller tur' />
        {<Button handleClick={(e) => reOrder(order.id, e)} modifiers={`${isBigCar ? 'tw-mr-auto ' : 'tw-ml-auto'}`} type='secondary' label={isBigCar ? 'Prøv igen' : 'Genbestil'} />}
        {isBigCar && <Button handleClick={replaceOrder} type='primary' label={'Ja, bestil 2 vogne'} />}
      </footer>
    </div>
  )
}

export default ReOrder